import { realtime } from './realtime.js';

// Thin singleton wrapper around one active board-game match. GameHallTables calls
// `attach(matchInfo)` the instant a match_started / match_resume arrives (BEFORE the
// scene transition) so the immediately-following per-socket match_state is captured
// and buffered — BoardGameScene then reads `.state` on create and subscribes for
// updates. One match is live at a time (server enforces one match per player), so a
// singleton is enough; `detach()` on leaving the scene drops the realtime handlers.
//
// Events (via `.on(evt, fn)`):
//   'state'                  -> match_state msg (board/turn/legal_moves/captured/your_color)
//   'over'                   -> match_over msg (result/winner/winner_name)
//   'opponent_disconnected'  -> match_opponent_disconnected (deadline_ts/player)
//   'opponent_reconnected'   -> match_opponent_reconnected (player)
//   'draw_offered'           -> match_draw_offered (match_id/by/by_name)
//   'draw_declined'          -> match_draw_declined (match_id)
//   'error'                  -> match_error (error_code) — server-side rejection of the
//                               last verb sent on THIS socket (move/offer/respond/claim/…)
class MatchClient {
  constructor() {
    this.info = null;        // {matchId, game, tableId, myColor, players}
    this.state = null;       // last match_state payload (buffered for the scene's first render)
    this._handlers = new Map();
    this._unsubs = [];
  }

  attach(info) {
    this.detach();
    this.info = info;
    this.state = null;
    this._unsubs = [
      realtime.on('match_state', (m) => {
        if (this.info && m.match_id !== this.info.matchId) return;
        this.state = m;
        // your_color is authoritative per-socket (match_state is the per-connection source
        // of truth even though match_resume now also sends a players map).
        if (this.info && m.your_color !== undefined) this.info.myColor = m.your_color;
        this._emit('state', m);
      }),
      realtime.on('match_over', (m) => {
        if (this.info && m.match_id !== this.info.matchId) return;
        if (this.info) this.info.over = true;   // main.js: a finished match no longer holds off "inactive"
        this._emit('over', m);
      }),
      // Disconnect/reconnect notices carry no match_id — they only reach this match's
      // table room, so no filter is needed.
      realtime.on('match_opponent_disconnected', (m) => this._emit('opponent_disconnected', m)),
      realtime.on('match_opponent_reconnected', (m) => this._emit('opponent_reconnected', m)),
      // Draw offers carry match_id, like match_state/match_over — filter the same way.
      realtime.on('match_draw_offered', (m) => {
        if (this.info && m.match_id !== this.info.matchId) return;
        this._emit('draw_offered', m);
      }),
      realtime.on('match_draw_declined', (m) => {
        if (this.info && m.match_id !== this.info.matchId) return;
        this._emit('draw_declined', m);
      }),
      // match_error carries NO match_id (see the comment block above) — it only ever
      // reaches the socket that sent the rejected verb, so it's delivered unfiltered.
      realtime.on('match_error', (m) => this._emit('error', m)),
    ];
  }

  on(evt, fn) {
    if (!this._handlers.has(evt)) this._handlers.set(evt, new Set());
    this._handlers.get(evt).add(fn);
    return () => this._handlers.get(evt)?.delete(fn);
  }

  _emit(evt, msg) {
    const set = this._handlers.get(evt);
    if (set) for (const fn of set) { try { fn(msg); } catch (e) { console.error(e); } }
  }

  move(path)  { realtime.matchMove(path); }
  resign()    { realtime.matchResign(); }
  claimWin()  { realtime.matchClaimWin(); }
  offerDraw()        { realtime.matchOfferDraw(); }
  respondDraw(accept) { realtime.matchRespondDraw(accept); }

  detach() {
    this._unsubs.forEach((u) => u?.());
    this._unsubs = [];
    this.info = null;
    this.state = null;
  }
}

export const matchClient = new MatchClient();
